import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import { getCorrelationCandidates, createCorrelation } from '../../api/wallet';
import { formatValue } from '../../utils/utils';
import { getErrorMessage } from '../../utils/errors';

const CorrelationSelector = ({
  open,
  onClose,
  parentId,
  parentDescription,
  categories,
  onCorrelationCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const [filters, setFilters] = useState({
    category: null,
    description: '',
    startDate: '',
    endDate: '',
  });

  const fetchCandidates = useCallback(async () => {
    if (!open || !parentId) return;

    setLoading(true);
    setError('');

    try {
      const params = {
        exclude_id: parentId,
      };

      if (filters.category) {
        params.category_id = filters.category.ID;
      }
      if (filters.description.length >= 3) {
        params.description = filters.description;
      }
      if (filters.startDate) {
        params.start_date = filters.startDate;
      }
      if (filters.endDate) {
        params.end_date = filters.endDate;
      }

      const response = await getCorrelationCandidates(params);
      setCandidates(response.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao buscar despesas disponiveis'));
    } finally {
      setLoading(false);
    }
  }, [open, parentId, filters]);

  useEffect(() => {
    if (open) {
      fetchCandidates();
    }
  }, [fetchCandidates, open]);

  useEffect(() => {
    if (!open) {
      setSelectedCandidate(null);
      setFilters({
        category: null,
        description: '',
        startDate: '',
        endDate: '',
      });
      setCandidates([]);
      setError('');
    }
  }, [open]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirm = async () => {
    if (!selectedCandidate) return;

    setCreating(true);
    setError('');

    try {
      await createCorrelation(parentId, selectedCandidate.id);
      onCorrelationCreated(true, selectedCandidate);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao criar correlacao'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Correlacionar Despesa
        <Typography variant="body2" color="text.secondary">
          Selecione uma despesa para correlacionar a &quot;{parentDescription}&quot;
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            placeholder="Buscar..."
            value={filters.description}
            onChange={(e) => handleFilterChange('description', e.target.value)}
            size="small"
            sx={{ width: 150 }}
          />
          <Autocomplete
            options={categories || []}
            getOptionLabel={(option) => option.Name || ''}
            value={filters.category}
            onChange={(e, newValue) => handleFilterChange('category', newValue)}
            isOptionEqualToValue={(option, value) => option.ID === value?.ID}
            size="small"
            sx={{ width: 150 }}
            renderInput={(params) => <TextField {...params} placeholder="Categoria" />}
          />
          <TextField
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            size="small"
            sx={{ width: 130 }}
            InputLabelProps={{ shrink: true }}
            placeholder="Data inicio"
          />
          <TextField
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            size="small"
            sx={{ width: 130 }}
            InputLabelProps={{ shrink: true }}
            placeholder="Data fim"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : candidates.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" py={4}>
            Nenhuma despesa disponivel para correlacao
          </Typography>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {candidates.map((candidate) => (
              <ListItem
                key={candidate.id}
                disablePadding
                secondaryAction={
                  <Typography variant="body2" color="error.main" fontWeight={500}>
                    {formatValue(candidate.value, candidate.currency)}
                  </Typography>
                }
              >
                <ListItemButton
                  selected={selectedCandidate?.id === candidate.id}
                  onClick={() => setSelectedCandidate(candidate)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{candidate.description}</Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={candidate.category_name} size="small" variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                          {candidate.debit_date}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedCandidate || creating}
        >
          {creating ? 'Criando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorrelationSelector;
